import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Image from "../../../assets/mofoIcon.png";
import { useAuth } from "@workspace/ui/hooks";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import MainLayout from "../../components/layout";

// 1. Define the schema
const loginSchema = z.object({
  username: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function Login({ appName }: { appName?: string }) {
  const [showEye, setShowEye] = useState(false);
  const { login, errorName, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // 2. Use react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  // 3. Handle form submit
  const onSubmit = async (data: LoginFormValues) => {
    if (loading) return;
    await login(data.username.trim().toLowerCase(), data.password);
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  return (
    <MainLayout showHeader={false}>
      <div className="flex flex-col justify-between bg-white px-4 py-6 sm:py-2">
        <div className="mx-auto w-full max-w-[480px] bg-white p-4 sm:p-6">
          <div className="flex flex-col gap-8 py-5">
            <img
              src={Image}
              alt="Modern Football Logo"
              className="mx-auto h-[35px] w-[120px] object-cover sm:h-[40px] sm:w-[130px]"
            />
          </div>

          {appName && (
            <h1 className="text-mofoBrandBlack text-center text-lg font-semibold sm:text-xl">
              {appName}
            </h1>
          )}
          <p className="mt-2 text-center text-sm font-normal leading-[22px] text-gray-700 sm:text-base sm:leading-[24px]">
            Enter your credentials to access your account.
          </p>

          <form className="mt-6 space-y-4" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 sm:text-base">Email</label>
              <input
                type="text"
                {...register("username")}
                autoComplete="off"
                className="mt-1 w-full rounded-md border border-gray-200 px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.username && <p className="text-sm text-red-500">{errors.username.message}</p>}
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 sm:text-base">Password</label>
              <div className="relative">
                <input
                  type={showEye ? "text" : "password"}
                  {...register("password")}
                  autoComplete="off"
                  className="mt-1 w-full rounded-md border border-gray-200 px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-500"
                  onClick={() => setShowEye(!showEye)}
                >
                  {showEye ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>

            {errorName && !errors.password && (
              <p className="mb-2 text-sm text-red-500 transition-opacity duration-300">
                {errorName}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full cursor-pointer items-center justify-center rounded-md bg-blue-600 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 sm:text-base"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Logging in...
                </div>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[#9CA3AF]">
            <Link to="/forgot-password">Reset Password</Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
